// constants/segmentOperators.js

import { IAttribute } from 'types/attributes/attributes.types';

// Field type mapping for auto-detection
export const FIELD_TYPES: Record<string, string> = {
  email: 'text',
  firstName: 'text',
  lastName: 'text',
  Plan: 'text',
  createdAt: 'date',
  updatedAt: 'date',
  lastLogin: 'date',
  totalOrders: 'number',
  revenue: 'number',
  age: 'number',
  isSubscribed: 'boolean',
  isActive: 'boolean',
  tags: 'list',
  interests: 'list',
};

export const SEGMENT_OPERATORS_GROUPS = {
  STRING_OR_NUMBER: 'String or Number',
  STRING: 'String',
  JSON_ARRAY: 'JSON array',
  JSON_OBJECT: 'JSON object',
  EMAIL: 'Email',
  UNIX_TIMESTAMP: 'Unix Timestamp',
  USER_DATA: 'User Data',
};
// Comprehensive operator definitions
export const SEGMENT_OPERATORS: Record<string, any> = {
  'String or Number': [
    {
      value: 'equals',
      label: 'is equal to',
      placeholder: 'Enter exact text',
    },
    {
      value: 'not_equals',
      label: 'is not equal to',
      placeholder: 'Enter text to exclude',
    },
    {
      value: 'greater_than',
      label: 'is greater than',
      placeholder: 'Enter number',
    },
    {
      value: 'less_than',
      label: 'is less than',
      placeholder: 'Enter number',
    },
    {
      value: 'between',
      label: 'is between',
      placeholder: 'Enter number range',
    },
    {
      value: 'exists',
      label: 'exists',
    },
    {
      value: 'not_exists',
      label: 'does not exist',
    },
    {
      value: 'is_empty',
      label: 'is empty',
    },
    {
      value: 'is_not_empty',
      label: 'is not empty',
    },
  ],
  String: [
    {
      value: 'contains',
      label: 'contains text',
      placeholder: 'Enter text to search for',
    },
    {
      value: 'not_contains',
      label: 'does not contain text',
      placeholder: 'Enter text to exclude',
    },
  ],
  'JSON array': [
    {
      value: 'at_least_one',
      label: 'where at least one',
      placeholder: 'Enter value to search for',
    },
  ],
  'JSON object': [
    {
      value: 'has_property',
      label: 'has the property',
      placeholder: 'Enter property name',
    },
  ],
  Email: [
    {
      value: 'is_valid',
      label: 'is valid and exists',
    },
    {
      value: 'is_invalid',
      label: 'is invalid or does not exist',
    },
  ],
  'Unix Timestamp': [
    {
      value: 'is_timestamp',
      label: 'is a Timestamp',
    },
    {
      value: 'is_not_timestamp',
      label: 'is not a Timestamp',
    },
    {
      value: 'is_before',
      label: 'is a timestamp before',
      placeholder: 'Enter date',
    },
    {
      value: 'is_after',
      label: 'is a timestamp after',
      placeholder: 'Enter date',
    },
    {
      value: 'is_between',
      label: 'is a timestamp between',
      placeholder: 'Enter date range',
    },
  ],
};

// Logical operators for combining conditions
export const CONDITION_OPTIONS = {
  'User Data': [
    {
      value: 'attribute',
      label: 'Attribute',
    },
  ],
  Segment: [
    {
      value: 'segment',
      label: 'Segment',
    },
  ],
};

// Operators when data source is Segment (field = segment, no value input)
export const SEGMENT_SOURCE_OPERATORS: Record<
  string,
  { value: string; label: string }[]
> = {
  Segment: [
    { value: 'in_segment', label: 'is in segment' },
    { value: 'not_in_segment', label: 'is not in segment' },
  ],
};

// Helper function to get operators by field name
export const getOperatorsByField = (fieldName: string) => {
  const fieldType = FIELD_TYPES[fieldName] || 'text';
  return SEGMENT_OPERATORS[fieldType] || SEGMENT_OPERATORS.text;
};

// Common field options for dropdowns
export const COMMON_FIELDS: IAttribute[] = [
  { name: '_id', label: '_id', isDeleted: false, tag: 'Identifier' },
  {
    name: 'custom_id',
    label: 'Custom ID',
    isDeleted: false,
    tag: 'Identifier',
  },
  { name: 'email', label: 'Email', isDeleted: false, tag: 'Identifier' },
  { name: 'firstName', label: 'First Name', isDeleted: false, tag: 'Reserved' },
  { name: 'lastName', label: 'Last Name', isDeleted: false, tag: 'Reserved' },
  {
    name: 'unsubscribed',
    label: 'Unsubscribed',
    isDeleted: false,
    tag: 'Reserved',
  },
  { name: 'createdAt', label: 'Created At', isDeleted: false, tag: 'Reserved' },
];

export const segmentConstants = {
  SEGMENT_OPERATORS,
  CONDITION_OPTIONS,
  FIELD_TYPES,
  getOperatorsByField,
  COMMON_FIELDS,
};
